import { useEffect, useState, type ComponentType } from "react";
import { Box, Card, CardActionArea, CardContent, Grid, IconButton, Typography } from "@mui/material";
import {
  Widgets,
  Search,
  BarChart,
  ShowChart,
  PieChart,
  ListAlt,
  AttachMoney,
  ShoppingCart,
  Percent,
  WarningAmber,
  TrendingDown,
  TrendingUp,
  Inventory2,
  Sell,
  LocalShipping,
  Description,
  TableChart,
  Star,
  StarBorder,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getAllModules, type ManifestModule } from "@/api/modules.service";
import { getModulesByUserAndClient } from "@/api/user.service";
import { getModulesByClient } from "@/api/client.service";
import { addFavoriteModule, getFavoriteModules, removeFavoriteModule } from "@/api/favorite.service";

// ponytail: manifest.icon vem do FontAwesome (usado no mobile via @expo/vector-icons),
// não existe libFA no web — mapeia só os nomes conhecidos, cai pro ícone genérico
// (Widgets) pros que ainda não conhecemos em vez de adicionar uma dependência nova.
// Upgrade path se isso ficar sempre defasado: trocar por react-icons/fa (mesmo set
// FontAwesome 4 do mobile) com lookup dinâmico "kebab-case" -> "FaPascalCase", que
// cobre qualquer ícone que o mobile cobre sem manter lista manual — custo: perde
// tree-shaking (import * dinâmico), adiciona ~algumas centenas de KB não comprimidos.
const ICON_MAP: Record<string, ComponentType<{ style?: React.CSSProperties }>> = {
  search: Search,
  "bar-chart": BarChart,
  "line-chart": ShowChart,
  "pie-chart": PieChart,
  list: ListAlt,
  dollar: AttachMoney,
  money: AttachMoney,
  "shopping-cart": ShoppingCart,
  percent: Percent,
  "exclamation-triangle": WarningAmber,
  warning: WarningAmber,
  "arrow-down": TrendingDown,
  "arrow-up": TrendingUp,
  cubes: Inventory2,
  archive: Inventory2,
  tags: Sell,
  truck: LocalShipping,
  "file-text": Description,
  clipboard: Description,
  table: TableChart,
};

function getModuleIcon(icon: string) {
  return ICON_MAP[icon] ?? Widgets;
}

// mirrors app-mobile/src/app/home.tsx sortModules — favorites first, then manifest order, then label
function sortModules(mods: ManifestModule[]) {
  return [...mods].sort((a, b) => {
    if (!!a.is_favorite !== !!b.is_favorite) return a.is_favorite ? -1 : 1;
    return (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label);
  });
}

interface ClienteModulesGridProps {
  // omit userId to show every module enabled for the client, regardless of user (admin view)
  userId?: string;
  clientId: string;
  variant?: "grid" | "list";
}

export function ClienteModulesGrid({ userId, clientId, variant = "grid" }: ClienteModulesGridProps) {
  const navigate = useNavigate();
  const [modules, setModules] = useState<ManifestModule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const modulesRequest = userId
      ? getModulesByUserAndClient(userId, clientId)
      : getModulesByClient(clientId);

    Promise.all([getAllModules(), modulesRequest])
      .then(([allModules, userModules]) => {
        if (cancelled) return;
        const allowedIds = new Set(userModules.map((m) => m.module_id));
        const allowedModules = allModules.filter((m) => allowedIds.has(m.id));
        setModules(sortModules(allowedModules));
        if (!userId) return;

        // favoritos são complementares — falha aqui não deve impedir a listagem de módulos
        getFavoriteModules()
          .then((favorites) => {
            if (cancelled) return;
            const favoriteIds = new Set(favorites.map((f) => f.module_id));
            setModules((prev) => sortModules(prev.map((m) => ({ ...m, is_favorite: favoriteIds.has(m.id) }))));
          })
          .catch(() => {});
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load client modules:", err);
          setModules([]);
        }
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [userId, clientId]);

  const toggleFavorite = (moduleId: string) => {
    const current = modules.find((m) => m.id === moduleId);
    if (!current) return;
    const nextFavorite = !current.is_favorite;

    setModules((prev) => sortModules(prev.map((m) => (m.id === moduleId ? { ...m, is_favorite: nextFavorite } : m))));

    (nextFavorite ? addFavoriteModule(moduleId) : removeFavoriteModule(moduleId)).catch(() => {
      setModules((prev) => sortModules(prev.map((m) => (m.id === moduleId ? { ...m, is_favorite: !nextFavorite } : m))));
    });
  };

  if (loading) return null;

  if (modules.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
        Nenhum módulo liberado para este cliente ainda.
      </Typography>
    );
  }

  if (variant === "list") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {modules.map((module) => {
          const Icon = getModuleIcon(module.icon);
          return (
            <Card
              key={module.id}
              elevation={0}
              sx={(theme) => ({
                position: "relative",
                borderRadius: 4,
                border: "1px solid",
                borderColor: theme.palette.divider,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              })}
            >
              {userId && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(module.id);
                  }}
                  sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                >
                  {module.is_favorite ? <Star fontSize="small" color="warning" /> : <StarBorder fontSize="small" />}
                </IconButton>
              )}
              <CardActionArea
                sx={(theme) => ({
                  p: 1,
                  "&:hover": { bgcolor: `${theme.palette.primary.main}08` },
                })}
                onClick={() => navigate(`/painel/${module.id}`)}
              >
                <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 3 }}>
                  <Box
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: 3,
                      color: theme.palette.primary.main,
                      bgcolor: `${theme.palette.primary.main}15`,
                    })}
                  >
                    <Icon style={{ width: 32, height: 32 }} />
                  </Box>
                  <Box sx={{ minWidth: 0, pr: userId ? 4 : 0 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {module.label}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    );
  }

  return (
    <Grid container spacing={2.5}>
      {modules.map((module) => {
        const Icon = getModuleIcon(module.icon);
        return (
          <Grid size={{ xs: 6, sm: 4, md: 3 }} key={module.id}>
            <Card
              elevation={0}
              sx={(theme) => ({
                position: "relative",
                height: "100%",
                width: "100%",
                borderRadius: 4,
                border: "1px solid",
                borderColor: theme.palette.divider,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                },
              })}
            >
              {userId && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavorite(module.id);
                  }}
                  sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                >
                  {module.is_favorite ? <Star fontSize="small" color="warning" /> : <StarBorder fontSize="small" />}
                </IconButton>
              )}
              <CardActionArea
                sx={(theme) => ({
                  height: "100%",
                  p: 1,
                  "&:hover": { bgcolor: `${theme.palette.primary.main}08` },
                })}
                onClick={() => navigate(`/painel/${module.id}`)}
              >
                <CardContent sx={{ p: 2.5, pr: userId ? 5 : 2.5 }}>
                  <Box
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 3,
                      mb: 2,
                      color: theme.palette.primary.main,
                      bgcolor: `${theme.palette.primary.main}15`,
                    })}
                  >
                    <Icon style={{ width: 26, height: 26 }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {module.label}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
